import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Group,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconDownload, IconRefresh } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { showNotification } from "@mantine/notifications";
import { useServerApi } from "../hooks/userServerApi";
import GISettlementStatementImporter from "./giSettlementStatementImporter";
import GISettlementCrossCheckRow from "./giSettlementCrossCheckRow";
import {
  buildOverdueSummary,
  filterRowsByOverdueBucket,
  formatOverdueBucketAmounts,
  getOverdueSummaryColor,
  getOverdueSummaryLabelKey,
  OVERDUE_BUCKETS,
  OVERDUE_SUMMARY_BUCKETS,
} from "../pages/gi/giSettlementCrossCheckUtils";
import { useFormatter } from "../hooks/useFomatter";

const MATCH_COVERAGE_BADGES = [
  {
    key: "total",
    labelKey: "giSettlement.crossCheck.summary.total",
    tooltipKey: "giSettlement.crossCheck.summary.totalHint",
    fallback: "Total",
    tooltipFallback: "All rows imported from the insurer statement",
    color: "gray",
  },
  {
    key: "matchedPending",
    labelKey: "giSettlement.crossCheck.summary.matchedPending",
    tooltipKey: "giSettlement.crossCheck.summary.matchedPendingHint",
    fallback: "In Pending",
    tooltipFallback:
      "Statement row matched to a debit note in the Pending settlement tab",
    color: "yellow",
  },
  {
    key: "matchedConfirm",
    labelKey: "giSettlement.crossCheck.summary.matchedConfirm",
    tooltipKey: "giSettlement.crossCheck.summary.matchedConfirmHint",
    fallback: "In Confirm",
    tooltipFallback:
      "Statement row matched to a debit note in the Confirm settlement tab",
    color: "green",
  },
  {
    key: "notInSettlement",
    labelKey: "giSettlement.crossCheck.summary.notInSettlement",
    tooltipKey: "giSettlement.crossCheck.summary.notInSettlementHint",
    fallback: "Not In Settlement",
    tooltipFallback:
      "Matching debit note found in the system but not in this settlement batch",
    color: "orange",
  },
  {
    key: "notFound",
    labelKey: "giSettlement.crossCheck.summary.notFound",
    tooltipKey: "giSettlement.crossCheck.summary.notFoundHint",
    fallback: "Not Found",
    tooltipFallback:
      "No matching debit note found in the system for this insurer",
    color: "red",
  },
  {
    key: "ambiguous",
    labelKey: "giSettlement.crossCheck.summary.ambiguous",
    tooltipKey: "giSettlement.crossCheck.summary.ambiguousHint",
    fallback: "Ambiguous",
    tooltipFallback:
      "Multiple debit notes matched — cannot auto-link to a single policy",
    color: "orange",
  },
];

const OverdueSummaryCard = ({
  label,
  value,
  amount,
  color,
  active,
  onClick,
}) => (
  <Card
    withBorder
    padding="sm"
    radius="md"
    onClick={onClick}
    sx={{
      minWidth: 0,
      overflow: "hidden",
      cursor: onClick ? "pointer" : undefined,
      ...(color &&
        (active
          ? {
              border: `2px solid var(--mantine-color-${color}-6)`,
            }
          : {
              borderLeft: `4px solid var(--mantine-color-${color}-6)`,
            })),
    }}
  >
    <Stack spacing={4}>
      <Title order={2} color={color} sx={{ lineHeight: 1 }}>
        {value ?? 0}
      </Title>
      <Text size="xs" fw={600} sx={{ lineHeight: 1.3, wordBreak: "break-word" }}>
        {amount}
      </Text>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Stack>
  </Card>
);

const MatchCoverageBadges = ({ summary, t }) => (
  <Group spacing="sm">
    {MATCH_COVERAGE_BADGES.map(
      ({ key, labelKey, tooltipKey, fallback, tooltipFallback, color }) => (
        <Tooltip
          key={key}
          label={t(tooltipKey, tooltipFallback)}
          withArrow
          multiline
          maw={280}
        >
          <Box component="span" sx={{ display: "inline-block", cursor: "help" }}>
            <Badge size="lg" variant="light" color={color}>
              {t(labelKey, fallback)}: {summary[key] ?? 0}
            </Badge>
          </Box>
        </Tooltip>
      )
    )}
  </Group>
);

const GISettlementCrossCheckTab = ({
  form,
  status,
  onRecheckComplete,
  rechecking,
  setRechecking,
}) => {
  const { t } = useTranslation();
  const [formatter] = useFormatter();
  const [api] = useServerApi();
  const [overdueBucket, setOverdueBucket] = useState("all");
  const [exporting, setExporting] = useState(false);

  const rows = form?.values?.insurerStatement?.rows || [];
  const summary = form?.values?.insurerCrossCheckSummary || {};
  const isCompleted = status === "COMPLETED";

  const overdueSummary = useMemo(() => buildOverdueSummary(rows), [rows]);

  const filteredRows = useMemo(
    () => filterRowsByOverdueBucket(rows, overdueBucket),
    [rows, overdueBucket]
  );

  const handleRecheck = async () => {
    const id = form?.values?._id;
    if (!id || isCompleted) return;
    setRechecking?.(true);
    const result = await api.GISettlement.crossCheck(id);
    setRechecking?.(false);
    if (result?.success) {
      form.setFieldValue("insurerStatement", result.data.insurerStatement);
      form.setFieldValue(
        "insurerCrossCheckSummary",
        result.data.insurerCrossCheckSummary
      );
      onRecheckComplete?.(result.data);
    }
  };

  const handleOverdueFilter = (bucketId) => {
    setOverdueBucket((current) => (current === bucketId ? "all" : bucketId));
  };

  const handleExportResult = async () => {
    const id = form?.values?._id;
    const code = form?.values?.code;
    if (!id || rows.length === 0 || exporting) return;

    setExporting(true);
    const result = await api.GISettlement.downloadCrossCheckResult(
      id,
      code ? `gi-settlement-cross-check-${code}.xlsx` : undefined
    );
    setExporting(false);

    if (result?.success) {
      showNotification({
        title: t("giSettlement.crossCheck.exportResult", "Export Result"),
        message: t(
          "giSettlement.crossCheck.exportSuccess",
          "Cross-check result exported"
        ),
        color: "green",
      });
      return;
    }

    showNotification({
      title: t("giSettlement.crossCheck.exportResult", "Export Result"),
      message:
        result?.error ||
        t("giSettlement.crossCheck.exportFailed", "Unable to export result"),
      color: "red",
    });
  };

  return (
    <Stack spacing="md" pt="xl">
      <Group position="apart">
        <GISettlementStatementImporter
          form={form}
          disabled={isCompleted}
          onImportSuccess={onRecheckComplete}
        />
        <Group spacing="xs">
          <Button
            size="xs"
            color="green"
            leftIcon={<IconDownload size={14} />}
            disabled={rows.length === 0 || exporting}
            loading={exporting}
            onClick={handleExportResult}
          >
            {t("giSettlement.crossCheck.exportResult", "Export Result")}
          </Button>
          <Button
            size="xs"
            variant="default"
            leftIcon={<IconRefresh size={14} />}
            disabled={isCompleted || rows.length === 0 || rechecking}
            loading={rechecking}
            onClick={handleRecheck}
          >
            {t("giSettlement.crossCheck.recheck", "Re-check")}
          </Button>
        </Group>
      </Group>

      {rows.length > 0 && (
        <>
          <Stack spacing="xs">
            <Text size="xs" c="dimmed" fw={500}>
              {t(
                "giSettlement.crossCheck.summary.overdueTitle",
                "Overdue severity"
              )}
            </Text>
            <SimpleGrid
              cols={5}
              breakpoints={[
                { maxWidth: "md", cols: 2 },
                { maxWidth: "sm", cols: 1 },
              ]}
            >
              {OVERDUE_SUMMARY_BUCKETS.map((bucket) => {
                const color = getOverdueSummaryColor(bucket.id);
                const bucketSummary = overdueSummary[bucket.id] || {
                  count: 0,
                  amountsByCurrency: {},
                };
                return (
                  <OverdueSummaryCard
                    key={bucket.id}
                    label={t(
                      getOverdueSummaryLabelKey(bucket.id),
                      bucket.label
                    )}
                    value={bucketSummary.count}
                    amount={formatOverdueBucketAmounts(
                      bucketSummary.amountsByCurrency,
                      formatter.currency
                    )}
                    color={color}
                    active={overdueBucket === bucket.id}
                    onClick={() => handleOverdueFilter(bucket.id)}
                  />
                );
              })}
            </SimpleGrid>
            <Text size="xs" c="dimmed" sx={{ minHeight: 18 }}>
              {overdueBucket !== "all" ? (
                <>
                  {t("giSettlement.crossCheck.filterActive", "Filtering by")}{" "}
                  {t(
                    getOverdueSummaryLabelKey(overdueBucket),
                    OVERDUE_BUCKETS.find((b) => b.id === overdueBucket)?.label
                  )}
                  {" · "}
                  <Text
                    component="span"
                    size="xs"
                    c="blue"
                    style={{ cursor: "pointer" }}
                    onClick={() => setOverdueBucket("all")}
                  >
                    {t("giSettlement.crossCheck.clearFilter", "Clear filter")}
                  </Text>
                </>
              ) : (
                t(
                  "giSettlement.crossCheck.showingAllOverdue",
                  "Showing all overdue buckets"
                )
              )}
            </Text>
          </Stack>

          <Stack spacing="xs">
            <Text size="xs" c="dimmed" fw={500}>
              {t("giSettlement.crossCheck.summary.matchTitle", "Match coverage")}
            </Text>
            <MatchCoverageBadges summary={summary} t={t} />
          </Stack>

          <ScrollArea type="auto">
            <Table fontSize="sm" striped highlightOnHover>
              <thead>
                <tr>
                  <th style={{ width: 40, minWidth: 40 }}>
                    <Text size="xs" ta="center">
                      #
                    </Text>
                  </th>
                  <th>
                    <Text size="xs">
                      {t(
                        "giSettlement.crossCheck.columns.policyNo",
                        "Policy No"
                      )}
                    </Text>
                  </th>
                  <th>
                    <Text size="xs">
                      {t(
                        "giSettlement.crossCheck.columns.statement",
                        "Statement"
                      )}
                    </Text>
                  </th>
                  <th>
                    <Text size="xs">
                      {t("giSettlement.crossCheck.columns.insurer", "Insurer")}
                    </Text>
                  </th>
                  <th>
                    <Text size="xs">
                      {t("giSettlement.crossCheck.columns.amount", "Amount")}
                    </Text>
                  </th>
                  <th>
                    <Text size="xs">
                      {t(
                        "giSettlement.crossCheck.columns.overdueDays",
                        "Overdue Days"
                      )}
                    </Text>
                  </th>
                  <th>
                    <Text size="xs">
                      {t(
                        "giSettlement.crossCheck.columns.matchStatus",
                        "Status"
                      )}
                    </Text>
                  </th>
                  <th style={{ width: 44, minWidth: 44 }}>
                    <Text size="xs" ta="center">
                      {t("giSettlement.crossCheck.columns.check", "Check")}
                    </Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row, index) => (
                  <GISettlementCrossCheckRow
                    key={`${row.policyNumber}-${row.insurerStatementCode}-${index}`}
                    row={row}
                    index={index}
                    t={t}
                  />
                ))}
              </tbody>
            </Table>
          </ScrollArea>

          {filteredRows.length === 0 && (
            <Text size="sm" c="dimmed" ta="center">
              {t(
                "giSettlement.crossCheck.noRowsInBucket",
                "No rows in this overdue bucket."
              )}
            </Text>
          )}
        </>
      )}

      {rows.length === 0 && (
        <Text size="sm" c="dimmed">
          {t(
            "giSettlement.crossCheck.empty",
            "Import an insurer statement to cross-check against Pending and Confirm debit notes."
          )}
        </Text>
      )}
    </Stack>
  );
};

export default GISettlementCrossCheckTab;
